import { useState, useEffect, useRef } from "react";
import { Info } from "@phosphor-icons/react";

/* ─────────────────────────────────────────────────────────────
   Diccionario completo de explicaciones (30 términos)
───────────────────────────────────────────────────────────── */
const EXPLANATIONS = {
  // ══ BÉISBOL (MLB) ══
  moneyline_mlb:
    "Apuesta al ganador del juego. Simple: ¿quién gana? No importa el marcador exacto ni los runs de diferencia.",
  run_line:
    "Hándicap de carreras. Siempre es -1.5 / +1.5. El favorito debe ganar por 2+ carreras. Ej: Yankees -1.5 → deben ganar por 2 o más para que ganes.",
  total_carreras:
    "Apuesta si las carreras totales de ambos equipos serán más o menos del número indicado. Ej: Más de 8.5 → necesitas 9 carreras o más en total.",
  primeros_5_innings:
    "Solo cuentan las primeras 5 entradas. Lo que pase después no importa. El ganador se determina por el marcador al finalizar el 5to inning.",
  total_hits:
    "Apuesta al total de hits (batazos conectados) de ambos equipos. Ej: Más de 15.5 hits → deben ocurrir 16 o más hits en todo el juego.",
  ambos_anotan_mlb:
    "Apuesta a que AMBOS equipos anoten al menos 1 carrera durante el juego. Si uno de los dos se queda en cero, pierdes.",
  home_runs_totales:
    "Apuesta al total de jonrones (home runs) en el partido. Ej: Más de 2.5 → deben caer 3 jonrones o más entre los dos equipos.",
  strikeouts_pitcher:
    "Apuesta a cuántos ponches (strikeouts) logrará el pitcher abridor. Ej: Más de 6.5 → el pitcher debe ponchar 7 o más bateadores.",
  resultado_exacto_mlb:
    "Apuesta al marcador exacto final. Ej: Yankees 5 – Red Sox 3. Es difícil de acertar, pero las cuotas son altas.",
  primera_carrera:
    "Apuesta a qué equipo anotará la primera carrera del juego. Solo importa el primer run, no el resultado final.",

  // ══ BÁSQUETBOL (NBA) ══
  moneyline_nba:
    "Apuesta al ganador directo del partido. No importa por cuántos puntos ganen, solo quién gana.",
  spread:
    "Hándicap de puntos. El favorito debe ganar por más puntos de los indicados. Ej: Lakers -5.5 → deben ganar por 6 o más puntos para que ganes.",
  total_puntos:
    "Apuesta si el total de puntos de ambos equipos será más o menos del número indicado. Ej: Más de 215.5 → deben sumarse 216 puntos o más.",
  gana_primera_mitad:
    "Solo cuentan los primeros 2 cuartos (primera mitad). El equipo que vaya ganando al medio tiempo gana esta apuesta.",
  gana_ultimo_cuarto:
    "Solo cuenta el 4to cuarto. El equipo que anote más puntos en ese cuarto gana, sin importar el marcador global.",
  triple_doble:
    "Apuesta a que un jugador logre 10+ en tres categorías (puntos, rebotes, asistencias). Ej: LeBron con 25pts, 10reb, 10ast = triple-doble.",
  margen_victoria:
    "Apuesta por cuántos puntos ganará el equipo. Ej: Lakers ganan por 6–10 puntos. El rango exacto debe cumplirse.",
  total_rebotes:
    "Apuesta al total de rebotes de ambos equipos o de un jugador. Ej: Más de 95.5 rebotes combinados en el partido.",
  primer_equipo_anotar:
    "Apuesta a qué equipo anotará la primera canasta del partido. Solo importa el primer punto, no el resultado final.",
  jugador_puntos:
    "Apuesta a si un jugador específico superará o no ciertos puntos. Ej: Curry Más de 25.5 → debe anotar 26 o más puntos.",

  // ══ FÚTBOL (SOCCER) ══
  "1x2":
    "Apuesta al resultado final del partido. 1 = Gana el equipo local, X = Empate, 2 = Gana el equipo visitante.",
  doble_oportunidad:
    "Cubres 2 de los 3 resultados posibles. Ej: 1X → ganas si el local gana o empata. Solo pierdes si gana el visitante.",
  over_under_goles:
    "Apuesta si el total de goles en el partido será más (Over) o menos (Under) del número indicado. Ej: Más de 2.5 → deben caer 3 goles o más.",
  ambos_marcan:
    "Apuesta a que AMBOS equipos anoten al menos 1 gol. Si alguno de los dos se queda en cero, pierdes.",
  handicap_asiatico:
    "Se da ventaja o desventaja de goles. Ej: Real Madrid -1.5 → deben ganar por 2 o más goles para que ganes. Elimina el empate.",
  resultado_exacto_soccer:
    "Apuesta al marcador exacto al final del tiempo reglamentario. Ej: Barcelona 2 – Atlético 1. Cuotas muy altas por ser difícil.",
  corners_totales:
    "Apuesta al total de tiros de esquina (córners) de ambos equipos. Ej: Más de 9.5 → deben ocurrir 10 córners o más en el partido.",
  gana_segunda_mitad:
    "Solo cuenta el segundo tiempo (últimos 45 min). Se ignora completamente el marcador del primer tiempo.",
  total_tarjetas:
    "Apuesta al total de tarjetas en el partido. Amarilla = 1 punto, Roja = 2 puntos. Ej: Más de 4.5 → equivale a 5 tarjetas o más.",
};

/* ─────────────────────────────────────────────────────────────
   Mapeo automático: market + sport → clave del diccionario
───────────────────────────────────────────────────────────── */
export function getTooltipTerm(market, sport) {
  if (!market) return null;
  const m = market.toLowerCase().trim();
  const s = (sport || "").toLowerCase();

  // Béisbol
  if (s === "mlb") {
    if (m.includes("moneyline") || m === "ml") return "moneyline_mlb";
    if (m.includes("run line") || m.includes("run_line") || m.includes("rl")) return "run_line";
    if (m.includes("5 inning") || m.includes("primeros 5") || m.includes("f5")) return "primeros_5_innings";
    if (m.includes("hit")) return "total_hits";
    if (m.includes("home run") || m.includes("jonron") || m.includes("hr")) return "home_runs_totales";
    if (m.includes("strikeout") || m.includes("ponche") || m.includes("k ")) return "strikeouts_pitcher";
    if (m.includes("primera carrera") || m.includes("first run")) return "primera_carrera";
    if (m.includes("ambos") || m.includes("btts") || m.includes("both")) return "ambos_anotan_mlb";
    if (m.includes("resultado exacto") || m.includes("exact")) return "resultado_exacto_mlb";
    if (m.includes("total") || m.includes("over") || m.includes("under") || m.includes("más de") || m.includes("menos de")) return "total_carreras";
  }

  // Básquetbol
  if (s === "nba") {
    if (m.includes("moneyline") || m === "ml") return "moneyline_nba";
    if (m.includes("spread") || m.includes("puntos de ventaja") || m.includes("hándicap") && !m.includes("asiat")) return "spread";
    if (m.includes("primera mitad") || m.includes("1a mitad") || m.includes("halftime") || m.includes("1er tiempo")) return "gana_primera_mitad";
    if (m.includes("cuarto") || m.includes("4to") || m.includes("quarter")) return "gana_ultimo_cuarto";
    if (m.includes("triple") && m.includes("doble")) return "triple_doble";
    if (m.includes("margen")) return "margen_victoria";
    if (m.includes("rebote")) return "total_rebotes";
    if (m.includes("primer") && (m.includes("anot") || m.includes("canas"))) return "primer_equipo_anotar";
    if (m.includes("jugador") || m.includes("player") || m.includes("pts") || (m.includes("puntos") && m.match(/\d/))) return "jugador_puntos";
    if (m.includes("total") || m.includes("over") || m.includes("under") || m.includes("más de") || m.includes("menos de")) return "total_puntos";
  }

  // Fútbol
  if (s === "football" || s === "soccer" || s === "futbol" || s === "fútbol") {
    if (m === "1x2" || m.includes("1 x 2") || m.includes("resultado final") || m.includes("1/x/2")) return "1x2";
    if (m.includes("doble oportunidad") || m.includes("double chance")) return "doble_oportunidad";
    if (m.includes("ambos marcan") || m.includes("btts") || m.includes("both teams")) return "ambos_marcan";
    if (m.includes("hándicap asiático") || m.includes("handicap asiatico") || m.includes("asian")) return "handicap_asiatico";
    if (m.includes("resultado exacto") || m.includes("correct score")) return "resultado_exacto_soccer";
    if (m.includes("córner") || m.includes("corner")) return "corners_totales";
    if (m.includes("segunda mitad") || m.includes("2a mitad") || m.includes("2do tiempo")) return "gana_segunda_mitad";
    if (m.includes("primera mitad") || m.includes("1a mitad") || m.includes("1er tiempo")) return "gana_primera_mitad";
    if (m.includes("tarjeta") || m.includes("card")) return "total_tarjetas";
    if (m.includes("total") || m.includes("over") || m.includes("under") || m.includes("más de") || m.includes("goles")) return "over_under_goles";
  }

  // Cross-sport fallback
  if (m.includes("moneyline") || m === "ml") return s === "nba" ? "moneyline_nba" : "moneyline_mlb";
  if (m.includes("spread")) return "spread";
  if (m === "1x2") return "1x2";
  if (m.includes("over") || m.includes("under") || m.includes("total")) {
    if (s === "nba") return "total_puntos";
    if (s === "mlb") return "total_carreras";
    return "over_under_goles";
  }

  return null;
}

/* ─────────────────────────────────────────────────────────────
   Componente InfoTooltip
   - Desktop: hover muestra el tooltip
   - Mobile: tap abre/cierra el tooltip
───────────────────────────────────────────────────────────── */
export default function InfoTooltip({ market, sport }) {
  const term = getTooltipTerm(market, sport);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Cierra al tocar fuera en mobile
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("touchstart", handler);
    document.addEventListener("mousedown", handler);
    return () => {
      document.removeEventListener("touchstart", handler);
      document.removeEventListener("mousedown", handler);
    };
  }, [open]);

  if (!term || !EXPLANATIONS[term]) return null;

  return (
    <>
      <span
        ref={ref}
        data-testid={`info-tooltip-${term}`}
        className="it-wrap"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onTouchStart={(e) => { e.stopPropagation(); setOpen((p) => !p); }}
        onClick={(e) => e.stopPropagation()}
        role="button"
        aria-label={`Explicación: ${market}`}
        tabIndex={0}
      >
        <Info weight="fill" className="it-icon" aria-hidden="true" />
        {open && (
          <span className="it-box" role="tooltip">
            <strong className="it-title">{market}</strong>
            {EXPLANATIONS[term]}
            <span className="it-arrow" aria-hidden="true" />
          </span>
        )}
      </span>

      <style>{`
        /* ── Wrapper ── */
        .it-wrap {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          flex-shrink: 0;
          cursor: help;
          vertical-align: middle;
          -webkit-tap-highlight-color: transparent;
        }

        /* ── Ícono ℹ ── */
        .it-icon {
          width: 14px;
          height: 14px;
          color: rgba(212,175,55,0.55);
          transition: color 0.15s, transform 0.15s;
          pointer-events: none;
        }
        .it-wrap:hover .it-icon,
        .it-wrap:focus .it-icon {
          color: #D4AF37;
          transform: scale(1.15);
        }

        /* ── Caja del tooltip ── */
        .it-box {
          position: absolute;
          bottom: calc(100% + 10px);
          left: 50%;
          transform: translateX(-50%);
          width: 280px;
          background: #111115;
          color: #e4e4e7;
          border: 1px solid rgba(212,175,55,0.45);
          border-radius: 10px;
          padding: 12px 14px;
          font-size: 12.5px;
          line-height: 1.55;
          font-weight: 400;
          letter-spacing: 0.01em;
          box-shadow: 0 8px 32px rgba(0,0,0,0.7), 0 0 0 1px rgba(212,175,55,0.1);
          z-index: 9999;
          pointer-events: none;
          white-space: normal;
          text-transform: none;
        }

        /* Título en negrita dorada */
        .it-title {
          display: block;
          color: #D4AF37;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          margin-bottom: 6px;
        }

        /* ── Flecha abajo del tooltip ── */
        .it-arrow {
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 7px solid transparent;
          border-top-color: rgba(212,175,55,0.45);
        }

        /* ── Ajuste de posición: si está muy a la izquierda → shift right ── */
        @media (max-width: 480px) {
          .it-box {
            width: 240px;
            font-size: 12px;
            left: auto;
            right: -8px;
            transform: none;
          }
          .it-arrow {
            left: auto;
            right: 14px;
            transform: none;
          }
        }
      `}</style>
    </>
  );
}
